import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import ServicePageLayout from '../../components/Layout/ServicePageLayout';
import { FaLaptop, FaReact, FaNodeJs, FaAws, FaDatabase, FaCode } from 'react-icons/fa';
import { SiNextdotjs, SiTypescript, SiTailwindcss, SiMongodb } from 'react-icons/si';
import { MdEmail, MdPhone, MdPerson, MdMessage } from 'react-icons/md';
import { submitToGoogleSheet } from '../../utils/helpers';
import classes from './ServicePage.module.scss';
import contactClasses from '../../components/Contact/Contact.module.scss';
import FAQ from '../../components/FAQ/FAQ';
import webDevHeroImg from '../../assets/web_dev_hero_img.png';
import artistNextDoorWeb from '../../assets/artist_next_door_web_portfolio.png';
import guruGaneshSaree from '../../assets/guruganeshsaree_portfolio.png';
import webDevScalable from '../../assets/web_dev_scalable_feature.png';
import webDevFastPerformance from '../../assets/web_dev_fast_performance_feature.png';
import webDevSeo from '../../assets/web_dev_seo_feature.png';
import webDevResponsive from '../../assets/web_dev_responsive_feature.png';

const WebDevelopment = () => {
  const { projects } = useSelector((state) => state.portfolio);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phoneNumber: '',
    message: '',
  });

  const [currentSlide, setCurrentSlide] = useState(0);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await submitToGoogleSheet(formData);
      if (typeof window.gtag_report_conversion === 'function') {
        window.gtag_report_conversion();
      }
      alert('Thank you! We will get back to you soon.');
      setFormData({ name: '', email: '', phoneNumber: '', message: '' });
    } catch (error) {
      console.error('Form submission error:', error);
      alert('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % webProjects.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + webProjects.length) % webProjects.length);
  };

  // Web projects with enhanced data structure for carousel
  const webProjects = [
    {
      id: 1,
      title: 'ARTIST NEXT DOOR',
      subtitle: 'Full-Stack Web Development',
      description: 'Comprehensive platform for artists to manage bookings, showcase portfolios, and connect with clients. Built with modern web technologies.',
      image: artistNextDoorWeb,
      category: 'Web',
      link: '/services/web-development',
      bgGradient: 'linear-gradient(135deg, rgb(133, 89, 200) 0%, rgb(52, 29, 89) 100%)'
    },
    {
      id: 2,
      title: 'E-COMMERCE STORE',
      subtitle: 'E-commerce Development',
      description: 'Modern e-commerce platform for traditional sarees with advanced filtering, wishlist, and seamless checkout experience.',
      image: guruGaneshSaree,
      category: 'Web',
      link: '/services/ecommerce-development',
      bgGradient: 'linear-gradient(135deg, rgb(198, 170, 125) 0%, rgb(200, 175, 140) 100%)'
    }
  ];

  const technologies = [
    { name: 'React', icon: <FaReact />, description: 'Modern frontend framework' },
    { name: 'Next.js', icon: <SiNextdotjs />, description: 'Full-stack React framework' },
    { name: 'Node.js', icon: <FaNodeJs />, description: 'Server-side JavaScript' },
    { name: 'TypeScript', icon: <SiTypescript />, description: 'Type-safe development' },
  ];

  const features = [
    {
      title: 'Responsive Design',
      description: 'Mobile-first approach ensuring perfect experience across all devices and screen sizes.',
      image: webDevResponsive
    },
    {
      title: 'SEO Optimized',
      description: 'Built with SEO best practices to ensure maximum visibility and search rankings.',
      image: webDevSeo
    },
    {
      title: 'Fast Performance',
      description: 'Optimized code and assets for lightning-fast loading speeds and user experience.',
      image: webDevFastPerformance
    },
    {
      title: 'Scalable Architecture',
      description: 'Modern architecture patterns that grow with your business needs.',
      image: webDevScalable
    }
  ];

  const faqs = [
    {
      question: 'How long does it take to build a web application?',
      answer: 'A simple website takes 4-6 weeks, while a full-stack web application with custom features, APIs, and integrations typically takes 2-4 months. Complex enterprise solutions may take longer depending on scope and requirements.'
    },
    {
      question: 'Do you build both frontend and backend?',
      answer: 'Yes. We handle the full stack — React/Next.js on the frontend, Node.js or Java Spring Boot on the backend, and databases like MongoDB, PostgreSQL, or Firebase. You get one team for the entire project.'
    },
    {
      question: 'Will my website be SEO-friendly?',
      answer: 'Absolutely. We build with SEO best practices from the start — server-side rendering with Next.js, semantic HTML, optimized meta tags, structured data, fast loading speeds, and mobile responsiveness — all crucial for search rankings.'
    },
    {
      question: 'Can you redesign or migrate my existing website?',
      answer: 'Yes, we handle full redesigns and platform migrations. We audit your current site, preserve SEO rankings, migrate content and data, and launch the new version with zero downtime.'
    },
    {
      question: 'Do you provide hosting and deployment support?',
      answer: 'Yes. We deploy to AWS, Vercel, Netlify, or your preferred cloud provider. We set up CI/CD pipelines, SSL certificates, CDN, monitoring, and provide ongoing maintenance and support.'
    },
    {
      question: 'What about website security?',
      answer: 'Security is built into every layer — HTTPS, input validation, SQL injection prevention, XSS protection, CSRF tokens, rate limiting, and secure authentication. We follow OWASP best practices throughout development.'
    },
  ];

  const process = [
    { step: '01', title: 'Requirements Analysis', description: 'Understanding business goals and technical requirements' },
    { step: '02', title: 'Architecture Design', description: 'Planning scalable and maintainable system architecture' },
    { step: '03', title: 'Frontend Development', description: 'Creating responsive and interactive user interfaces' },
    { step: '04', title: 'Backend Development', description: 'Building robust APIs and server-side logic' },
    { step: '05', title: 'Testing & QA', description: 'Comprehensive testing for functionality and performance' },
    { step: '06', title: 'Deployment & Launch', description: 'Cloud deployment with monitoring and support' },
  ];

  return (
    <ServicePageLayout>
      <div className={classes.servicePageContainer}>
        {/* Hero Section */}
        <section className={classes.hero}>
          <div className={classes.container}>
            <div className={classes.heroContent}>
              <div className={classes.heroHighlight}>
                <div className={classes.heroText}>

                  <h1 className={classes.heroTitle}>
                    Web Application <span className={classes.gradient}>Development</span>
                  </h1>
                  <p className={classes.heroSubtitle}>
                    Build powerful, scalable web applications that drive business growth. From simple websites
                    to complex enterprise solutions, we deliver exceptional digital experiences.
                  </p>
                  <div className={classes.heroCTA}>
                    <button
                      className={classes.exploreButton}
                      onClick={() => document.getElementById('features').scrollIntoView({ behavior: 'smooth' })}
                    >
                      Explore Services
                    </button>
                    <button
                      className={classes.quoteButton}
                      onClick={() => document.getElementById('contact-form').scrollIntoView({ behavior: 'smooth' })}
                    >
                      Get A Quote
                    </button>
                  </div>
                  <div className={classes.heroStats}>
                    <div className={classes.stat}>
                      <span className={classes.statNumber}>{webProjects.length}+</span>
                      <span className={classes.statLabel}>Web Projects</span>
                    </div>
                    <div className={classes.stat}>
                      <span className={classes.statNumber}>4000+</span>
                      <span className={classes.statLabel}>Active Users</span>
                    </div>
                    <div className={classes.stat}>
                      <span className={classes.statNumber}>372</span>
                      <span className={classes.statLabel}>Cities Reached</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className={classes.heroVisual}>
                <div className={classes.phoneContainer}>
                  <img
                    src={webDevHeroImg}
                    alt="Web Development"
                    className={classes.phoneImage}
                  />
                </div>
              </div>
            </div>
          </div>
        </section>


        {/* Features Section */}
        <section id="features" className={classes.features}>
          <div className={classes.container}>
            <div className={classes.sectionHeader}>
              <h2 className={classes.sectionTitle}>
                Why Choose Our <span className={classes.gradient}>Web Development</span>
              </h2>
            </div>
            <div className={classes.featuresGrid}>
              {features.map((feature, index) => (
                <div key={index} className={classes.featureCard}>
                  <div className={classes.featureImageContainer}>
                    <img
                      src={feature.image}
                      alt={feature.title}
                      className={classes.featureImage}
                    />
                  </div>
                  <h3 className={classes.featureTitle}>{feature.title}</h3>
                  <p className={classes.featureDescription}>{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>


        {/* Technologies Section */}
        <section className={classes.technologies}>
          <div className={classes.container}>
            <div className={classes.sectionHeader}>
              <h2 className={classes.sectionTitle}>
                Modern <span className={classes.gradient}>Technologies</span>
              </h2>
              <p className={classes.sectionSubtitle}>
                Leveraging the latest web technologies to build future-ready applications
              </p>
            </div>
            <div className={classes.techGrid}>
              {technologies.map((tech, index) => (
                <div key={index} className={classes.techCard}>
                  <div className={classes.techIcon}>{tech.icon}</div>
                  <h3 className={classes.techName}>{tech.name}</h3>
                  <p className={classes.techDescription}>{tech.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Projects Section */}
        <section className={classes.portfolioSection}>
          <div className={classes.portfolioContainer}>
            <div className={classes.portfolioHeader}>
              <h2 className={classes.portfolioTitle}>
                Our Web <span className={classes.gradient}>Projects</span>
              </h2>
            </div>

            <div className={classes.carousel}>
              <div className={classes.slideContainer}>
                <div
                  className={classes.slides}
                  style={{ transform: `translateX(-${currentSlide * 100}%)` }}
                >
                  {webProjects.map((project, index) => (
                    <div
                      key={project.id}
                      className={classes.slide}
                      style={{ background: project.bgGradient }}
                    >
                      <div className={classes.slideContent}>
                        <div className={classes.slideTop}>
                          <div className={classes.imageContainer}>
                            <img src={project.image} alt={project.title} />
                          </div>
                        </div>

                        <div className={classes.slideBottom}>
                          <div className={classes.slideHeader}>
                            <h3 className={classes.projectTitle}>{project.title}</h3>
                            <span className={classes.categoryBadge}>{project.category}</span>
                          </div>

                          <h4 className={classes.projectSubtitle}>{project.subtitle}</h4>

                          <p className={classes.projectDescription}>{project.description}</p>

                          <a href={project.link} className={classes.learnMoreBtn}>
                            <span>Learn More</span>
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                              <path d="M6 12L10 8L6 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          </a>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className={classes.navigation}>
                <button
                  className={`${classes.navBtn} ${classes.prevBtn}`}
                  onClick={prevSlide}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>

                <button
                  className={`${classes.navBtn} ${classes.nextBtn}`}
                  onClick={nextSlide}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              </div>

              <div className={classes.indicators}>
                {webProjects.map((_, index) => (
                  <button
                    key={index}
                    className={`${classes.indicator} ${index === currentSlide ? classes.active : ''}`}
                    onClick={() => setCurrentSlide(index)}
                  />
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Process Section */}
        <section className={classes.process}>
          <div className={classes.container}>
            <div className={classes.sectionHeader}>
              <h2 className={classes.sectionTitle}>
                Our Development <span className={classes.gradient}>Process</span>
              </h2>
              <p className={classes.sectionSubtitle}>
                A systematic approach ensuring successful web application delivery
              </p>
            </div>
            <div className={classes.processGrid}>
              {process.map((step, index) => (
                <div key={index} className={classes.processStep}>
                  <div className={classes.stepNumber}>{step.step}</div>
                  <h3 className={classes.stepTitle}>{step.title}</h3>
                  <p className={classes.stepDescription}>{step.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <FAQ faqs={faqs} title="Frequently Asked" highlight="Questions" />

        {/* Contact Form Section */}
        <section id="contact-form" className={contactClasses.contact}>
          <div className={contactClasses.container}>
            <div className={contactClasses.header}>
              <span className={contactClasses.badge}>Get Your Quote</span>
              <h2 className={contactClasses.title}>
                Let's Build Your <span className={contactClasses.gradient}>Web Application</span>
              </h2>
            </div>

            <div className={contactClasses.content}>
              <form className={contactClasses.form} onSubmit={handleSubmit}>
                <div className={contactClasses.formRow}>
                  <div className={contactClasses.formGroup}>
                    <label htmlFor="name">
                      <MdPerson className={contactClasses.labelIcon} />
                      Full Name
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="Enter your full name"
                      className={contactClasses.premiumInput}
                    />
                  </div>

                  <div className={contactClasses.formGroup}>
                    <label htmlFor="email">
                      <MdEmail className={contactClasses.labelIcon} />
                      Email Address
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="your.email@company.com"
                      className={contactClasses.premiumInput}
                    />
                  </div>
                </div>

                <div className={contactClasses.formGroup}>
                  <label htmlFor="phoneNumber">
                    <MdPhone className={contactClasses.labelIcon} />
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    id="phoneNumber"
                    name="phoneNumber"
                    value={formData.phoneNumber}
                    onChange={handleChange}
                    required
                    placeholder="+1 (555) 123-4567"
                    className={contactClasses.premiumInput}
                  />
                </div>

                <div className={contactClasses.formGroup}>
                  <label htmlFor="message">
                    <MdMessage className={contactClasses.labelIcon} />
                    Web Development Details
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows="6"
                    placeholder="Tell us about your web application vision - features, timeline, integrations, and any specific technical requirements..."
                    className={contactClasses.premiumTextarea}
                  ></textarea>
                </div>

                <button type="submit" className={contactClasses.submitBtn} disabled={loading}>
                  <span className={contactClasses.btnText}>
                    {loading && <span className={contactClasses.spinner} />}
                    {loading ? 'Sending...' : 'Get My Quote'}
                  </span>
                  <div className={contactClasses.btnGlow}></div>
                </button>
              </form>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className={classes.cta}>
          <div className={classes.container}>
            <div className={classes.ctaContent}>
              <h2 className={classes.ctaTitle}>
                Ready to Build Your <span className={classes.gradient}>Web Application</span>?
              </h2>
              <p className={classes.ctaSubtitle}>
                Let's create a powerful web solution that drives your business forward
              </p>
              <a href="#contact-form" className={classes.ctaButton}>Start Your Project</a>
            </div>
          </div>
        </section>
      </div>
    </ServicePageLayout>
  );
};

export default WebDevelopment;